import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Box from '@mui/material/Box';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import MuiButton from '@mui/material/Button';
import { NotificationManager } from '../../../ui/toast';
import { TextInput } from '../../../ui/input/text_input/text_input';
import LoadingSpinner from '../../job_openings/create/ui/LoaderSpinner';
import {
  getAnalyticsSettings,
  updateAnalyticsSettings,
} from '../../../utils/api_service/organization_analytics_settings';

export const AnalyticsSettings = () => {
  const [hourlyRate, setHourlyRate] = useState<string>('');
  const [currency, setCurrency] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);

  const handleSaveButton = () => {
    if (hourlyRate && currency) {
      updateAnalyticsSettings(hourlyRate, currency)
        .then(() => {
          NotificationManager.success('Settings saved successfully!');
        })
        .catch(() => {
          NotificationManager.error('Unable to save settings!');
        });
    }
  };

  useEffect(() => {
    setLoading(true);
    getAnalyticsSettings()
      .then((response) => {
        if (response?.hourlyRate) {
          setHourlyRate(response.hourlyRate);
        }
        if (response?.currency) {
          setCurrency(response.currency);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      <Box sx={{ width: '100%', borderBottom: '1px solid', borderColor: 'divider', py: 1.5 }}>
        <Breadcrumbs aria-label='breadcrumb' separator='/'>
          <Link
            component={RouterLink}
            to='#'
            underline='hover'
            sx={{ fontSize: '0.875rem', color: 'text.secondary' }}
          >
            Admin
          </Link>
          <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: 'text.primary' }}>
            Analytics Settings
          </Typography>
        </Breadcrumbs>
      </Box>

      {loading ? (
        <Paper
          variant='outlined'
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: 350,
            gap: 2,
            bgcolor: 'background.paper',
            borderRadius: '6px',
            borderColor: 'divider',
            p: 4,
          }}
        >
          <LoadingSpinner label='' />
          <Typography
            sx={{
              fontSize: '0.75rem',
              fontWeight: 600,
              color: 'text.secondary',
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
            }}
          >
            Loading Analytics Settings...
          </Typography>
        </Paper>
      ) : (
        <Paper
          variant='outlined'
          sx={{
            p: 3,
            borderRadius: '6px',
            borderColor: 'divider',
            bgcolor: 'background.paper',
            minHeight: 400,
          }}
        >
          <Typography sx={{ fontSize: '0.875rem', color: 'text.secondary', mb: 3 }}>
            You can change the hourly rate and currency below.
          </Typography>
          <Stack spacing={2.5} sx={{ maxWidth: 480 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography sx={{ fontSize: '0.8125rem', fontWeight: 500, color: 'text.primary' }}>
                Hourly Rate
              </Typography>
              <TextInput
                name='hourlyRate'
                value={hourlyRate || ''}
                type='number'
                onChange={(event) => setHourlyRate(event.target.value)}
              />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography sx={{ fontSize: '0.8125rem', fontWeight: 500, color: 'text.primary' }}>
                Currency
              </Typography>
              <TextInput
                name='currency'
                value={currency}
                type='text'
                onChange={(event) => setCurrency(event.target.value)}
              />
            </Box>
            <Box sx={{ display: 'flex', pt: 1 }}>
              <MuiButton
                onClick={handleSaveButton}
                variant='contained'
                sx={{
                  borderRadius: '6px',
                  textTransform: 'none',
                  fontWeight: 600,
                  bgcolor: '#093725',
                  '&:hover': { bgcolor: '#062619' },
                  px: 3,
                }}
              >
                Save
              </MuiButton>
            </Box>
          </Stack>
        </Paper>
      )}
    </Box>
  );
};
